import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import type { RootState } from 'src/redux/rootReducerType';
import DataVaultContext from 'src/contexts/DataVaultContext';
import {
  fetchProfileSurvey,
  selectProfileSurvey,
} from 'src/redux/slices/DataVault/dataVaultProfileSurveySlice';
import {
  fetchElevationStatus,
  selectIsElevated,
  selectElevationStatus,
  selectElevationExpiresAt,
  selectHasTotp,
} from 'src/redux/slices/DataVault/dataVaultElevationSlice';
import {
  selectHasPendingVerifications,
  selectNewRenterInterfaceUser,
  setHasPendingVerifications,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import VerifyCodeModal from 'src/components/NewRenterInterface/AccountHub/VerifyCodeModal';
import PendingVerificationsModal from 'src/components/Ulcm/PendingVerificationsModal';
import useAuth from 'src/hooks/useAuth';
import { useProfileWebSocket } from 'src/hooks/DataVaultHooks/useProfileWebSocket';

export function DataVaultLayout(): React.JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { memberId } = useParams<{ memberId?: string }>();
  const { user: authUser } = useAuth();
  const actingOnMemberId = memberId ? parseInt(memberId, 10) : null;

  // Use the acting-on member's user id if in member mode, otherwise use current user
  const targetUserId = actingOnMemberId ?? authUser?.id ?? 0;
  const isOnBehalf = !!actingOnMemberId;
  useProfileWebSocket(targetUserId);

  const profileEntry = useSelector(selectProfileSurvey(targetUserId));

  // Elevation gate (Doc 13)
  const elevated = useSelector(selectIsElevated);
  const elevationStatus = useSelector(selectElevationStatus);
  const expiresAt = useSelector(selectElevationExpiresAt);
  const hasTotp = useSelector(selectHasTotp);
  const elevationGateEnabled = useSelector(
    (state: RootState) =>
      state.featureFlags.flags?.['data_vault.elevation_gate_enabled'] ?? true
  );

  // Contact verification gate — shown before elevation prompt when the user
  // has pending contact verifications that block OTP dispatch.
  const [showPendingVerifications, setShowPendingVerifications] =
    useState(false);
  const hasPendingVerifications = useSelector(selectHasPendingVerifications);

  // Use Redux user (updated by UserUpdateManager after contact verification)
  // rather than useAuth().user (JWT) which stays stale until next login.
  const reduxUser = useSelector(selectNewRenterInterfaceUser);

  // Prefer SMS when the user has no real deliverable email address.
  const hasRealEmail =
    !!authUser?.email && !authUser.email.endsWith('@prontohousing.io');
  const otpFirstChannel: 'email' | 'sms' = hasRealEmail ? 'email' : 'sms';

  // The user can receive an OTP if they have a verified email (real, not
  // a placeholder) OR a verified phone. Only when BOTH are missing does the
  // pending-verifications backlog actually block them.
  const hasUsableContact =
    (hasRealEmail && reduxUser?.email_verified === true) ||
    reduxUser?.phone_verified === true;
  const blockOnPending = hasPendingVerifications && !hasUsableContact;

  // Fetch elevation status on mount
  useEffect(() => {
    if (elevationGateEnabled) {
      dispatch(fetchElevationStatus());
    }
  }, [dispatch, elevationGateEnabled]);

  // Profile survey fetch (existing behavior)
  useEffect(() => {
    dispatch(fetchProfileSurvey({ targetUserId, actingOnMemberId }));
  }, [dispatch, targetUserId, actingOnMemberId]);

  // Schedule re-fetch 60 seconds before expiry
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!elevationGateEnabled || !elevated || !expiresAt) {
      return;
    }
    const expiryMs = new Date(expiresAt).getTime();
    const refetchAt = expiryMs - 60_000;
    const delay = refetchAt - Date.now();
    if (delay > 0) {
      refetchTimerRef.current = setTimeout(() => {
        dispatch(fetchElevationStatus());
      }, delay);
    }
    return (): void => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }
    };
  }, [dispatch, elevated, expiresAt, elevationGateEnabled]);

  // Re-fetch on tab visibility change
  useEffect(() => {
    if (!elevationGateEnabled) {
      return;
    }
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchElevationStatus());
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return (): void => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [dispatch, elevationGateEnabled]);

  // Show loading while elevation status is being fetched
  if (
    elevationGateEnabled &&
    (elevationStatus === 'loading' || elevationStatus === 'idle')
  ) {
    return <div data-testid="dv-elevation-loading" aria-busy="true" />;
  }

  // Show elevation prompt if not elevated
  if (elevationGateEnabled && !elevated) {
    if (showPendingVerifications || blockOnPending) {
      return (
        <>
          <div data-testid="dv-elevation-loading" aria-busy="true" />
          <PendingVerificationsModal
            onAllVerified={() => {
              dispatch(setHasPendingVerifications(false));
              setShowPendingVerifications(false);
              dispatch(fetchElevationStatus());
            }}
            onDismiss={() => {
              dispatch(setHasPendingVerifications(false));
              setShowPendingVerifications(false);
              navigate('/renter');
            }}
          />
        </>
      );
    }

    return (
      <VerifyCodeModal
        purpose="elevated_auth"
        totpEnabled={hasTotp}
        preferredChannel={otpFirstChannel}
        onSuccess={() => {
          dispatch(fetchElevationStatus());
        }}
        onUnverifiedContact={() => setShowPendingVerifications(true)}
        onSkip={() => navigate('/renter')}
      />
    );
  }

  return (
    <DataVaultContext.Provider
      value={{
        surveyId: profileEntry?.data?.id ?? null,
        targetUserId,
        isOnBehalf,
        actingOnMemberId,
      }}
    >
      <div
        data-dd-privacy="mask-user-input"
        role="region"
        aria-label="My Data"
        style={{ height: '100%' }}
      >
        <Outlet />
      </div>
    </DataVaultContext.Provider>
  );
}
